import { useEffect, useState } from "react";

export interface NearViewport {
  /** Callback ref: attach to the element whose visibility matters. */
  ref: (node: HTMLElement | null) => void;
  /**
   * False only when a real observer has reported the element more than one
   * viewport away. True until proven otherwise.
   */
  isNear: boolean;
}

/**
 * Mobile playback windowing (2026-09-25 mobile tile layout spec): reports
 * whether the attached element is within ~one viewport-height of the
 * viewport (`rootMargin: "100%"`). Team Live tiles use `isNear` to
 * mount/unmount their WHEP player as they scroll in and out of range.
 *
 * jsdom and older browsers lack IntersectionObserver; there (and when
 * `enabled` is false, i.e. desktop) we always report near so nothing ever
 * goes dormant.
 */
export function useNearViewport(enabled: boolean): NearViewport {
  const [isNear, setIsNear] = useState(true);
  const [node, setNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled) {
      setIsNear(true);
      return;
    }
    if (!node || typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setIsNear(entry.isIntersecting),
      { rootMargin: "100%", threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, node]);

  return { ref: setNode, isNear };
}
